import { BCMSCurrency, Branding, useBCMSModal, useModel, useStateManager, useUIState } from "@bcm-dev/bcms-vite";
import { TableItemTextView, TableItemView } from "components/shared";
import React from "react";
import { ImageAsset, SVGAsset } from "res";
import CustomTableAccordion, { CustomTableContent, CustomTableToggle } from "../CustomTableAccordion";
import "./styles.css";

/**
 * @name CustomTransactionAccordion
 * @function
 * @param {Transaction} transaction - Instance of the transaction model class to populate data
 * @returns {React.Component}
 * @description Main component for the custom transaction accordion
 * @example <CustomTransactionAccordion transaction={ transaction } />
 */
export default function CustomTransactionAccordion(props) {
    const uiStateConfig = getTransactionAccordionConfig(props.transaction.id);
    const { config, key } = useUIState(uiStateConfig.key, uiStateConfig.value);
    const account = props.account ?? {};

    return (
        <CustomTableAccordion activeClass="opened" state={ config.accordionActive }>
            <CustomTransactionRow transaction={ props.transaction } uiKey={ key } config={ config } account={ account } />
            <CustomTransactionDetails transaction={ props.transaction } uiKey={ key } config={ config } account={ account } />
        </CustomTableAccordion>
    );
}

export const getTransactionAccordionConfig = id => ({
    key: `TransactionItem_${ id }`,
    value: {
        accordionActive: false,
        showChecks: false
    }
})

/**
 * @name CustomTransactionRow
 * @function
 * @param {Transaction} transaction - Instance of the transaction modal class to populate data
 * @returns {React.Component}
 * @description The main row for a transaction
 * @example <CustomTransactionRow transaction={ transaction } />
 */
export function CustomTransactionRow({ transaction, uiKey, config, account }) {
    const { onItemClick } = useStateManager();

    const date = transaction.date.format("m/d/Y") ?? "[[MM/DD/YYYY]]";
    let description = transaction.description  ?? "";
    description = transaction.status === "pending" ? description + " (Pending)" : description;
    const type = transaction.type ?? "";
    const amount = transaction.amount.format() ?? "$X.XX";
    const balance = transaction.balance.amount !== undefined ? transaction.balance.format() : "";
    const id = transaction.id ?? "";

    const isDeposit = transaction.filterType === "deposit";
    const toggleClick = value => onItemClick(uiKey, "accordionActive", value);
    const defaultAmount =  BCMSCurrency.init(0);
    const principal = transaction.principal ?? BCMSCurrency.init(0);
    const interest = transaction.interest ?? BCMSCurrency.init(0);

    return (
        <CustomTableToggle onClick={ toggleClick }>
            <tr className="table-item-container">
                <TableItemView className="expand-row-chevron text-center" data-point={`Chevron_Icon_${id}`}>
                    <SVGAsset id={ config.accordionActive ? "DownChevronIcon" : "RightChevronIcon" } className="icon" />
                </TableItemView>

                <TableItemTextView className="text-left date" text={ date } />
                <TableItemTextView className={ `text-left description ${ account.type === "loan" || account.type === "loc" ? "loan" : "" }` } text={ description } />
                { account.type !== "loc" && <TableItemTextView className="text-left type" text={ transaction.type } /> }
                <TableItemTextView className={ `text-right amount ${ isDeposit && "deposit-text" }` } text={ amount } dataPoint={`Amount_Total_${id}`}/>
                { (account.type !== "loan" && account.type !== "loc") &&  <TableItemTextView className="text-right balance" text={ balance } /> }
                { (account.type === "loan") && 
                <>
                <TableItemTextView className="text-right principal" text={ defaultAmount.format() } />
                <TableItemTextView className="text-right interest" text={ defaultAmount.format() } />
                <TableItemTextView className="text-right escrow" text={ defaultAmount.format() } />
                </>
                }
                { (account.type === "loc") && 
                <>
                <TableItemTextView className="text-right principal" text={ principal.format() } />
                <TableItemTextView className="text-right interest" text={ interest.format() } />
                <TableItemTextView className="text-right balance" text={ balance } />
                </>
                }
            </tr>
        </CustomTableToggle>
    )
}

/**
 * @name CustomTransactionDetails
 * @function
 * @param {Transaction} transacton - Instance of the transaction model class to populate data
 * @returns {React.Component}
 * @description Component for the transaction details
 * @example <CustomTransactionDetails transaction={ transaction } />
 */
export function CustomTransactionDetails({ transaction, uiKey, config, account }) {
    const date = transaction.date.format("m/d/Y") ?? "[[MM/DD/YYYY]]";
    const postedDate = transaction.status === "pending" ? "Pending" : date;
    const id = transaction.id ?? "";
  const { open } = useBCMSModal();
    const { ui, onItemClick, onTransactionInquiryModalSendButtonClick } = useStateManager();
 const { branding } = useModel();
const options = branding.options;
 const inquiryLabel = options.transaction_inquiry_label_abi22 ?? "Transaction Inquiry"; 
    const toggleClick = value => onItemClick(uiKey, "accordionActive", value);
    const checkClick = evt => onItemClick(uiKey, "showChecks", !config.showChecks);
    const showInquiry =  prop => val => {
        open("TransactionInquiryModal", { view: prop, onSendClick: onTransactionInquiryModalSendButtonClick });
      };
      
        return (
        <CustomTableContent>
            <tr className="custom-transaction-content table-item-container">
                <td colSpan={ account.type === "loan" || account.type === "loc" ? "8" : "6" }>
                    <div className="flex-container flex-horizontal details-row">
                        <h3 className="flex-width" data-point={`Transaction_Details_${id}`}>TRANSACTION DETAILS</h3>
                        
                        <CustomTableToggle onClick={ toggleClick }>
                            <SVGAsset id="CloseX" className="icon" data-point={`Close_Transaction_${id}`}/>
                        </CustomTableToggle>
                    </div>

                    <div className="flex-container flex-vertical date-row">
                        <p>Transaction Date</p>
                        <p><b>{ date }</b></p>
                    </div>

                    <div className="flex-container flex-vertical date-row">
                        <p>Posted Date</p>
                        <p><b>{ postedDate }</b></p>
                    </div>

                    <div className="flex-container flex-vertical date-row">
                        <p>Type</p>
                        <p><b>{ transaction.type }</b></p>
                    </div>

                    { transaction.type === "check" &&
                        <Branding id="view_check_images_abi22" value="true">
                            <div className="flex-container flex-horizontal link-spacing">
                                <a className="clickable" data-point={ `ViewCheck_${ id }` } onClick={ checkClick }>{ config.showChecks ? "Hide" : "View" } Check</a>
                            </div>
                        </Branding> }
                       
                       
                    { config.showChecks &&
                        <>
                            <div className="flex-container flex-horizontal check-row">
                                <ImageAsset id="CheckFront" data-point={"Front_Check"}/>
                            </div>

                            <div className="flex-container flex-horizontal check-row">
                                <ImageAsset id="CheckBack"  data-point={"Back_Check"}/>
                            </div>
                        </> } {(transaction.filterType !=="deposit" && transaction?.account?.type !=="loan" && transaction?.account?.type !=="loc") &&   <Branding id="transaction_inquiry_abi22" value="true">
                            <div className="flex-container flex-horizontal link-spacing">
                                <a className="clickable" data-point={ `showInquiry_${ id }` } onClick={ showInquiry(transaction) }>{inquiryLabel}</a>
                            </div>
                        </Branding> }
                </td>
            </tr>
        </CustomTableContent>
    );
}